import React, { useEffect, useRef } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Mention from '@tiptap/extension-mention';
import { Extension } from '@tiptap/core';
import { Plugin } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import Toolbar from './Toolbar';
import './editorStyles.scss';

/**
 * Placeholder sin @tiptap/extension-placeholder (no está instalada): con el documento vacío marca su primer
 * párrafo con `is-editor-empty` y `data-placeholder`, que es lo que lee el CSS de editorStyles.scss.
 */
const EditorPlaceholder = Extension.create({
  name: 'editorPlaceholder',
  addOptions() {
    return { placeholder: '' };
  },
  addProseMirrorPlugins() {
    const { placeholder } = this.options;
    return [
      new Plugin({
        props: {
          decorations: ({ doc }) => {
            const primero = doc.firstChild;
            const vacio = doc.childCount === 1 && primero?.isTextblock && primero.content.size === 0;
            if (!placeholder || !vacio) return null;
            return DecorationSet.create(doc, [
              Decoration.node(0, primero.nodeSize, { class: 'is-editor-empty', 'data-placeholder': placeholder }),
            ]);
          },
        },
      }),
    ];
  },
});

const createMentionSuggestion = () => {
  let popup;
  let list;
  let propsRef;
  let selectedIndex = 0;

  const updateList = () => {
    if (!list || !propsRef) return;
    list.innerHTML = '';
    const items = propsRef.items || [];
    if (!items.length) {
      const empty = document.createElement('li');
      empty.className = 'bitacora-editor-mention-empty';
      empty.textContent = 'Sin resultados';
      list.appendChild(empty);
      return;
    }

    items.forEach((item, index) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = `bitacora-editor-mention-item${index === selectedIndex ? ' is-selected' : ''}`;
      button.textContent = `@${item.display}`;
      button.addEventListener('mousedown', (event) => {
        event.preventDefault();
        propsRef.command(item);
      });
      const li = document.createElement('li');
      li.appendChild(button);
      list.appendChild(li);
    });
  };

  const updatePopupPosition = () => {
    if (!popup || !propsRef?.clientRect) return;
    const rect = propsRef.clientRect();
    if (!rect) return;
    popup.style.left = `${rect.left + window.scrollX}px`;
    popup.style.top = `${rect.bottom + window.scrollY + 6}px`;
  };

  return {
    items: ({ query, editor }) => {
      const users = editor?.storage?.bitacoraMentionUsers || [];
      const normalizedQuery = (query || '').toLowerCase();
      return users
        .filter((item) => item?.display?.toLowerCase().includes(normalizedQuery))
        .slice(0, 6);
    },
    render: () => ({
      onStart: (props) => {
        propsRef = props;
        selectedIndex = 0;
        popup = document.createElement('div');
        popup.className = 'bitacora-editor-mention-popup';
        list = document.createElement('ul');
        list.className = 'bitacora-editor-mention-list';
        popup.appendChild(list);
        document.body.appendChild(popup);
        updateList();
        updatePopupPosition();
      },
      onUpdate: (props) => {
        propsRef = props;
        const maxIndex = Math.max((props.items?.length || 1) - 1, 0);
        selectedIndex = Math.min(selectedIndex, maxIndex);
        updateList();
        updatePopupPosition();
      },
      onKeyDown: (props) => {
        const items = props.items || [];
        if (props.event.key === 'Escape') {
          popup?.remove();
          return true;
        }
        if (!items.length) return false;
        if (props.event.key === 'ArrowDown') {
          selectedIndex = (selectedIndex + 1) % items.length;
          updateList();
          return true;
        }
        if (props.event.key === 'ArrowUp') {
          selectedIndex = (selectedIndex - 1 + items.length) % items.length;
          updateList();
          return true;
        }
        if (props.event.key === 'Enter') {
          props.command(items[selectedIndex]);
          return true;
        }
        return false;
      },
      onExit: () => {
        popup?.remove();
      },
    }),
    command: ({ editor, range, props }) => {
      editor
        .chain()
        .focus()
        .insertContentAt(range, [
          { type: 'mention', attrs: { id: props.id, label: props.display } },
          { type: 'text', text: ' ' },
        ])
        .run();
    },
  };
};

const TiptapEditor = ({
  value = '',
  onChange,
  onDebouncedChange,
  debounceMs = 700,
  placeholder = 'Escribe aquí...',
  disabled = false,
  contentClassName = '',
  toolbarExtra = null,
  mentionUsers = [],
  variableTags = [],
}) => {
  const debounceTimerRef = useRef(null);
  const contentClasses = ['bitacora-editor-content', contentClassName].filter(Boolean).join(' ');

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3, 4] },
        blockquote: false,
      }),
      Underline,
      EditorPlaceholder.configure({ placeholder }),
      Mention.configure({
        HTMLAttributes: {
          class: 'bitacora-editor-mention',
        },
        renderText: ({ options, node }) => `${options.suggestion.char}${node.attrs.label ?? node.attrs.id ?? ''}`,
        suggestion: {
          char: '@',
          ...createMentionSuggestion(),
        },
      }),
    ],
    content: value || '<p></p>',
    editable: !disabled,
    onUpdate: ({ editor: tiptapEditor }) => {
      const html = tiptapEditor.getHTML();
      if (onChange) onChange(html);

      if (onDebouncedChange) {
        if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = setTimeout(() => {
          onDebouncedChange(html);
        }, debounceMs);
      }
    },
    editorProps: {
      attributes: {
        class: contentClasses,
        'data-placeholder': placeholder,
      },
      handleDrop: (view, event) => {
        const variableValue = event.dataTransfer?.getData?.('application/x-cobranza-variable');
        if (variableValue) {
          event.preventDefault();
          const coords = { left: event.clientX, top: event.clientY };
          const pos = view.posAtCoords(coords);
          if (pos != null) {
            view.focus();
            const { state } = view;
            const tr = state.tr.insertText(variableValue, pos.pos);
            view.dispatch(tr);
            return true;
          }
        }
        return false;
      },
      handleDragOver: (view, event) => {
        if (event.dataTransfer?.types?.includes('application/x-cobranza-variable')) {
          event.preventDefault();
          event.dataTransfer.dropEffect = 'copy';
          return true;
        }
        return false;
      },
    },
  });

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (!editor) return;
    editor.setEditable(!disabled);
  }, [editor, disabled]);

  useEffect(() => {
    if (!editor) return undefined;

    const handleModZ = (event) => {
      const isMod = event.metaKey || event.ctrlKey;
      const key = String(event.key || '').toLowerCase();
      if (!isMod || key !== 'z') return;

      event.preventDefault();
      if (event.shiftKey) editor.commands.redo();
      else editor.commands.undo();
    };

    const dom = editor.view?.dom;
    dom?.addEventListener('keydown', handleModZ);
    return () => dom?.removeEventListener('keydown', handleModZ);
  }, [editor]);

  useEffect(() => {
    if (!editor) return;
    const normalizedIncoming = value || '<p></p>';
    const current = editor.getHTML();
    if (current !== normalizedIncoming) {
      editor.commands.setContent(normalizedIncoming, false);
    }
  }, [editor, value]);

  useEffect(() => {
    if (!editor) return;
    editor.storage.bitacoraMentionUsers = mentionUsers || [];
  }, [editor, mentionUsers]);

  const insertVariable = (variableValue, pos) => {
    if (!editor) return;
    const text = typeof variableValue === 'string' ? variableValue : `{{${variableValue}}}`;
    if (pos != null) {
      editor.chain().focus().insertContentAt(pos, text).run();
    } else {
      editor.chain().focus().insertContent(text).run();
    }
  };

  return (
    <div className={`bitacora-editor ${disabled ? 'is-disabled' : ''}`}>
      <div className="bitacora-editor-toolbar-row">
        <Toolbar editor={editor} disabled={disabled} />
        {toolbarExtra && <div className="bitacora-editor-toolbar-extra">{toolbarExtra}</div>}
      </div>
      {variableTags?.length > 0 && (
        <div className="bitacora-editor-variable-tags">
          <span className="bitacora-editor-variable-tags-label">Insertar:</span>
          {variableTags.map((tag) => {
            const value = typeof tag === 'string' ? tag : tag.value;
            const label = typeof tag === 'string' ? tag : tag.label;
            const varText = value.includes('{{') ? value : `{{${value}}}`;
            return (
              <span
                key={value}
                className="bitacora-editor-variable-tag"
                draggable
                onDragStart={(e) => {
                  e.dataTransfer.setData('application/x-cobranza-variable', varText);
                  e.dataTransfer.effectAllowed = 'copy';
                }}
                onClick={() => insertVariable(varText)}
                title={`Arrastrar o hacer clic para insertar ${varText}`}
              >
                {label || varText}
              </span>
            );
          })}
        </div>
      )}
      <div className="bitacora-editor-body">
        <EditorContent editor={editor} />
      </div>
    </div>
  );
};

export default TiptapEditor;
